import { CHAT_SOCKET_EVENT, type ChatSocketServerEnvelope } from './chatSocketTypes';

const LIST_EVENTS = new Set<string>([
  CHAT_SOCKET_EVENT.ChatNew,
  CHAT_SOCKET_EVENT.ChatUpdated,
  CHAT_SOCKET_EVENT.ChatDeleted,
]);

export type ChatRealtimeChannel = 'all' | 'list' | 'unread' | 'reconnect';

export type ChatRealtimeSignal =
  | { kind: 'event'; envelope: ChatSocketServerEnvelope }
  | { kind: 'reconnect' };

type ChatRealtimeListener = (signal: ChatRealtimeSignal) => void;

type Subscription = {
  channels: Set<ChatRealtimeChannel>;
  listener: ChatRealtimeListener;
};

const subscriptions = new Set<Subscription>();
const pendingListEvents: ChatSocketServerEnvelope[] = [];
const PENDING_LIST_EVENTS_MAX = 100;
let lastUnreadTotalEnvelope: ChatSocketServerEnvelope<'chat:total-unread-count'> | null = null;

function rememberUnreadTotalEnvelope(
  envelope: ChatSocketServerEnvelope<'chat:total-unread-count'>,
) {
  lastUnreadTotalEnvelope = envelope;
}

function replayUnreadTotalToSubscriber(listener: ChatRealtimeListener) {
  if (!lastUnreadTotalEnvelope) return;
  listener({ kind: 'event', envelope: lastUnreadTotalEnvelope });
}

function hasListConsumer(): boolean {
  for (const sub of subscriptions) {
    if (sub.channels.has('all') || sub.channels.has('list')) {
      return true;
    }
  }

  return false;
}

function deliverEnvelope(
  envelope: ChatSocketServerEnvelope,
  channel: ChatRealtimeChannel,
) {
  for (const sub of subscriptions) {
    if (sub.channels.has('all') || sub.channels.has(channel)) {
      sub.listener({ kind: 'event', envelope });
    }
  }
}

export function emitChatRealtime(envelope: ChatSocketServerEnvelope) {
  if (LIST_EVENTS.has(envelope.event)) {
    if (!hasListConsumer()) {
      pendingListEvents.push(envelope);

      if (pendingListEvents.length > PENDING_LIST_EVENTS_MAX) {
        pendingListEvents.shift();
      }

      return;
    }

    deliverEnvelope(envelope, 'list');
    return;
  }

  if (envelope.event === CHAT_SOCKET_EVENT.UnreadTotalUpdated) {
    rememberUnreadTotalEnvelope(
      envelope as ChatSocketServerEnvelope<'chat:total-unread-count'>,
    );
    deliverEnvelope(envelope, 'unread');
    return;
  }

  deliverEnvelope(envelope, 'all');
}

export function emitChatRealtimeReconnect() {
  for (const sub of subscriptions) {
    if (sub.channels.has('all') || sub.channels.has('reconnect')) {
      sub.listener({ kind: 'reconnect' });
    }
  }
}

export function subscribeChatRealtime(
  listener: ChatRealtimeListener,
  channels: ChatRealtimeChannel[] = ['all'],
) {
  const sub: Subscription = {
    channels: new Set(channels),
    listener,
  };

  subscriptions.add(sub);

  const consumesList = sub.channels.has('all') || sub.channels.has('list');

  if (consumesList && pendingListEvents.length) {
    const pending = [...pendingListEvents];
    pendingListEvents.length = 0;
    pending.forEach((envelope) => listener({ kind: 'event', envelope }));
  }

  if (sub.channels.has('unread') || sub.channels.has('all')) {
    replayUnreadTotalToSubscriber(listener);
  }

  return () => {
    subscriptions.delete(sub);
  };
}

export function clearChatRealtimePending() {
  pendingListEvents.length = 0;
  lastUnreadTotalEnvelope = null;
}

export function resetChatRealtimeBus() {
  subscriptions.clear();
  pendingListEvents.length = 0;
  lastUnreadTotalEnvelope = null;
}
